import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    this.#applyDisplaySettings(this.querySelector('.price-with-savings'));

    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * @returns {boolean}
   */
  #shouldShowCompareAtPrice() {
    return this.dataset.showCompareAtPrice !== 'false';
  }

  /**
   * @returns {boolean}
   */
  #shouldShowSavingsBadge() {
    return this.dataset.showSavingsBadge === 'true';
  }

  /**
   * Removes compare-at price and savings badge nodes when their settings are off.
   * @param {Element | null | undefined} priceRoot
   */
  #applyDisplaySettings(priceRoot) {
    if (!(priceRoot instanceof Element)) return;

    if (!this.#shouldShowCompareAtPrice()) {
      priceRoot.querySelectorAll('.compare-at-price').forEach((compareElement) => {
        const compareGroup = compareElement.closest('[role="group"]');
        if (compareGroup?.querySelector('.compare-at-price')) {
          compareGroup.remove();
        } else {
          compareElement.remove();
        }
      });
    }

    if (!this.#shouldShowSavingsBadge()) {
      priceRoot.querySelectorAll('.savings-badge').forEach((badge) => badge.remove());
    }
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (!event.detail?.data?.html) return;

    const sourceProductPrice =
      event.detail.data.html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`) ||
      event.detail.data.html.querySelector(`product-price[data-product-id="${this.dataset.productId}"]`) ||
      event.detail.data.html.querySelector('product-price');
    if (!sourceProductPrice) return;

    // Try to find the new price element in the event data
    let newPrice = sourceProductPrice.querySelector('.price-with-savings');
    let currentPrice = this.querySelector('.price-with-savings');

    // Fallback to priceContainer if price-with-savings doesn't exist
    if (!newPrice || !currentPrice) {
      newPrice = sourceProductPrice.querySelector('[ref="priceContainer"]');
      currentPrice = this.querySelector('[ref="priceContainer"]');
    }

    if (!newPrice || !currentPrice) return;

    const sanitizedPrice = /** @type {Element} */ (newPrice.cloneNode(true));
    this.#applyDisplaySettings(sanitizedPrice);

    if (currentPrice.innerHTML !== sanitizedPrice.innerHTML) {
      currentPrice.replaceWith(sanitizedPrice);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
